// IMPORT React
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  CustomAsyncSelect,
  CustomSelect,
} from "../../../components/CustomFields/CustomSelect";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { CustomTextField } from "../../../components/CustomFields/TextField";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import {
  GetMedicineDetail,
  CreateMedicine,
  UpdateMedicine,
} from "../../../services/MedicineService";
import { PreferencesContext } from "../../../PreferenceContext";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import { PAGE_SIZE } from "../../../services/common/const";
import { GetMedicineGroupList } from "../../../services/MedicineGroupService";
// import { GetMedicineUsedAsList } from "../../../services/MedicineUsedAsService";
import { GetMedicineFormulationList } from "../../../services/MedicineFormulationService";
import { GetMedicineGenericNameList } from "../../../services/MedicineGenericNameService";
import MedicineCompanySave from "../Medicinecompany/MedicinecompanySave";
import MedicineGenericNameSave from "../MedicineGenericName/MedicineGenericNameSave";
import MedicineGroupSave from "../MedicineGroup/MedicineGroupSave";
import MedicineFormulationSave from "../MedicineFormulation/MedicineFormulationSave";
import { GetMedicineCompanyList } from "../../../services/MedicineCompanyService";
import { packageTypes } from "../../../shared/Data/Package";
import CustomTooltip from "../../../components/ToolTip/CustomToolTip";
import { handleFocus } from "../../../shared/CommonFunction";
import { createInputPropsForNumberField } from "../../../shared/data";
import {
  GetPackagePerVolumeUnitList,
  GetPackageUnitList,
  GetPerVolumeUnitList,
  GetPrintUnitList,
  GetStrengthUnitList,
} from "../../../services/MasterService";
import DraggableList from "../../../components/Draggable/DraggableList";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { GetMedicineDoseList } from "../../../services/MedicineDoseService";
import MedicineDoseSave from "../MedicineDose/MedicineDoseSave";
import { components } from "react-select";
// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  medicineId: string;
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
  saveType: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  medicineId: "",
  consumerId: "",
  orgId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
  saveType: "SAVE",
};

interface IMedicineSaveProps {
  isForPopUp?: boolean;
  handleClosePopUp: (code: string, data?: any) => void;
  name: string;
  genericNameObject?: any;
  popUpMedicineId?: number;
  isFrom?: string;
}

interface IMedicineMedicineDose {
  medicineDoseTitle: string;
  medicineDoseId: number;
  label: string;
  value: number;
  displayOrder: number;
  medicineGenericName: string;
  weightBsaDependentDesiredDoseValue: number;
  weightBsaDependentMinDoseValue: number;
  weightBsaDependentDesiredDoseUnitName: string;
  weightBsaDependentPerWeightBsaUnitName: string;
  weightBsaDependentPerIntervalUnitName: string;
  weightBsaDependentMaxDoseValue: number;
  fixedDoseValue: number;
  maxFixedDoseValue: number;
  fixedDoseUnitName: string;
  fixedDosePerIntervalUnitName: string;
  maxFixedDoseUnitName: string;
  maxFixedDosePerIntervalUnitName: string;
  isDeleted: boolean;
  mode: string;
}

// SCHEMA FOR FORM
interface formInitialValueTypes {
  medicineId: number;
  medicineLabel: string;
  consumerId: number;
  orgId: number;
  branchId: number;
  genericNameId: selectOption | null;
  genericName: string | null;
  brandName: string;
  medicineGroupId: selectOption | null;
  medicineGroupName: string | null;
  doseDecidingContent: string | null;
  displayMedicineGenericName: string | null;
  // medicineUsedAsId: selectOption | null;
  medicineFormulationId: selectOption | null;
  medicineFormulationName: string | null;
  strengthValue: number | null;
  strengthUnitId: selectOption | null;
  strengthUnitName: string | null;
  unitPerVolumeValue: number | null;
  unitPerVolumeUnitId: selectOption | null;
  unitPerVolumeUnitName: string | null;
  medicineCompanyId: selectOption | null;
  medicineCompanyName: string | null;
  packageAmountOfContent: number | null;
  packageUnitPerVolume: selectOption | null;
  packageUnitPerVolumeName: string | null;
  packageType: selectOption | null;
  packageTypeName: string | null;
  dosePrintUnit: selectOption | null;
  dosePrintUnitName: string | null;
  previousMedicineId: number | null;
  nextMedicineId: number | null;
  isDeleted: boolean;
  medicineMedicineDoseColl: Array<IMedicineMedicineDose>;
  fileteredMedicineMedicineDoseColl: Array<IMedicineMedicineDose>;
}

export default function MedicineSave(props: IMedicineSaveProps) {
  // PROPS
  const {
    isForPopUp = false,
    handleClosePopUp,
    name,
    genericNameObject = null,
    popUpMedicineId = 0,
    isFrom = "",
  } = props;

  // DEFAULT SCHEMA VALUE FOR FORM
  const initMedicineData: formInitialValueTypes = {
    medicineId: 0,
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    genericNameId: isForPopUp ? genericNameObject : null,
    brandName: isForPopUp ? name?.toUpperCase() : "",
    medicineGroupId: null,
    // medicineUsedAsId: null,
    medicineFormulationId: null,
    strengthValue: null,
    strengthUnitId: { label: "mg", value: "MG" },
    unitPerVolumeValue: null,
    unitPerVolumeUnitId: { label: "ml", value: "ML" },
    medicineCompanyId: null,
    packageAmountOfContent: null,
    packageUnitPerVolume: { label: "ml", value: "ML" },
    packageUnitPerVolumeName: "ml",
    packageType: null,
    isDeleted: false,
    genericName: isForPopUp ? genericNameObject?.label?.toUpperCase() : null,
    medicineCompanyName: null,
    medicineGroupName: null,
    doseDecidingContent: null,
    displayMedicineGenericName: null,
    medicineFormulationName: null,
    strengthUnitName: "mg",
    unitPerVolumeUnitName: "ml",
    packageTypeName: null,
    dosePrintUnit: null,
    dosePrintUnitName: null,
    previousMedicineId: null,
    nextMedicineId: null,
    medicineMedicineDoseColl: [],
    fileteredMedicineMedicineDoseColl: [],
  };

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  //INPUT PROPS
  const numberProps = createInputPropsForNumberField(18, 4, true);

  // GETTING PARAMS
  let medicineId = 0;
  let copyMedicineId = 0;
  let urlParams = useQuery();

  if (!isForPopUp) {
    let curRawMedicineId = urlParams.get("q");
    let curRawCopyMedicineId = urlParams.get("c");
    if (curRawMedicineId !== null && curRawMedicineId !== undefined) {
      let decId = edService.decrypt(curRawMedicineId);
      if (decId !== "" && decId !== null && decId !== undefined) {
        medicineId = +decId;
      }
      if (curRawCopyMedicineId !== null && curRawCopyMedicineId !== undefined) {
        let decId2 = edService.decrypt(curRawCopyMedicineId);
        if (decId2 !== "" && decId2 !== null && decId2 !== undefined) {
          copyMedicineId = +decId2;
        }
      }
    } else {
      if (urlParams.size > 0) {
        navigate("/not-found");
      }
    }
  } else {
    if (popUpMedicineId > 0) {
      medicineId = popUpMedicineId;
    }
  }

  const theme = useTheme();
  // BREAK POINTS
  // const lgd = useMediaQuery(theme.breakpoints.down("lg"));
  const mdd = useMediaQuery(theme.breakpoints.down("md"));
  const smd = useMediaQuery(theme.breakpoints.down("sm"));

  // STATES FOR DATA STORAGE  -- PLEASE USE PROPER TYPES FOR STATE
  const [medicineGenericNameData, setMedicineGenericNameData] = useState<any[]>(
    [],
  );
  const [medicineCompanyNameData, setMedicineCompanyNameData] = useState<any[]>(
    [],
  );
  const [medicineGroupData, setMedicineGroupData] = useState<any[]>([]);
  // const [medicineUsedAsData, setMedicineUsedAsData] = useState<any[]>([]);
  const [medicineFormulationData, setMedicineFormulationData] = useState<any[]>(
    [],
  );
  const [param, setParam] = useState(parameters);

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
    genericNameObject: null,
    medicineDoseId: 0,
  });

  const [strengthUnitColl, setStrengthUnitColl] = useState(
    userContext?.strengthUnitColl || [],
  );

  const [perVolumeUnitColl, setPerVolumeUnitColl] = useState(
    userContext?.perVolumeUnitColl || [],
  );

  const [printUnitColl, setPrintUnitColl] = useState(
    userContext?.printUnitColl || [],
  );

  const [packagePerVolumeUnitColl, setPackagePerVolumeUnitColl] = useState(
    userContext?.packagePerVolumeUnitColl || [],
  );

  const [packageUnitColl, setPackageUnitColl] = useState(
    userContext?.packageUnitColl || [],
  );

  const [medicineDoseColl, setMedicineDoseColl] = useState<any[]>([]);
  const handleClickOpen = (
    value: boolean,
    label: string,
    code: string,
    genericNameObject?: any,
    medicineDoseId?: number,
  ) => {
    setCustomPopUp({
      ...customPopUp,
      open: value,
      name: label,
      code: code,
      genericNameObject: genericNameObject,
      medicineDoseId: medicineDoseId || 0,
    });
  };

  const handleClose = () => {
    setCustomPopUp({ ...customPopUp, open: false });
  };

  const handleManagePopup = async (code, option) => {
    setCustomPopUp({ ...customPopUp, open: false });
    setIsLoading(true);

    if (code === "MEDICINE_GENERIC_CREATE") {
      methods.setValue(
        "genericNameId",
        {
          label: option?.medicineGenericLable,
          value: option?.medicineGenericId?.toString(),
        },
        {
          shouldValidate: true,
        },
      );
      methods.setValue("genericName", option?.medicineGenericLable, {
        shouldValidate: true,
      });
      await fetchMedicineGenericName("", true);
    } else if (code === "MEDICINE_COMPANY_CREATE") {
      methods.setValue(
        "medicineCompanyId",
        {
          label: option?.medicinCompanyLable,
          value: option?.medicineCompanyId?.toString(),
        },
        {
          shouldValidate: true,
        },
      );
      methods.setValue("medicineCompanyName", option?.medicinCompanyLable, {
        shouldValidate: true,
      });
      await fetchMedicineCompanyName("", true);
    } else if (code === "MEDICINE_GROUP_CREATE") {
      methods.setValue(
        "medicineGroupId",
        {
          label: option?.medicineGroupLable,
          value: option?.medicineGroupId?.toString(),
        },
        {
          shouldValidate: true,
        },
      );
      methods.setValue("medicineGroupName", option?.medicineGroupLable, {
        shouldValidate: true,
      });
      await fetchMedicineGroup("", true);
    } else if (code === "MEDICINE_FORMULATION_CREATE") {
      methods.setValue(
        "medicineFormulationId",
        {
          label: option?.medicineFormulationLable,
          value: option?.medicineFormulationId?.toString(),
        },
        {
          shouldValidate: true,
        },
      );
      methods.setValue(
        "medicineFormulationName",
        option?.medicineFormulationLable,
        {
          shouldValidate: true,
        },
      );
      await fetchMedicineFormulation("", true);
    } else if (code === "MEDICINE_DOSE_CREATE") {
      if (customPopUp?.medicineDoseId > 0) {
        await getMedicineDetail();
      } else {
        await fetchMedicineDose(
          methods.getValues("genericNameId")?.value || "0",
          "",
          true,
        );
      }
    }
    setIsLoading(false);
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    itemId: Yup.number().nullable(),
    consumerId: Yup.number().required(),
    branchId: Yup.number().required(),
    orgId: Yup.number().required(),
    genericNameId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    genericName: Yup.string().nullable(),
    brandName: Yup.string().required("Brand Name is required"),
    medicineGroupName: Yup.string().nullable(),
    doseDecidingContent: Yup.string().nullable(),
    displayMedicineGenericName: Yup.string().nullable(),
    medicineGroupId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    medicineFormulationName: Yup.string().nullable(),
    medicineFormulationId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    strengthValue: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .nullable(),
    strengthUnitId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    strengthUnitName: Yup.string().nullable(),
    unitPerVolumeValue: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .nullable(),
    unitPerVolumeUnitId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    unitPerVolumeUnitName: Yup.string().nullable(),
    medicineCompanyName: Yup.string().nullable(),
    medicineCompanyId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    packageAmountOfContent: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .nullable(),
    packageUnitPerVolume: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    packageUnitPerVolumeName: Yup.string().nullable(),
    packageTypeName: Yup.string().nullable(),
    packageType: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    dosePrintUnitName: Yup.string().nullable(),
    dosePrintUnit: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    medicineMedicineDoseColl: Yup.array()
      .of(
        Yup.object().shape({
          medicineDoseTitle: Yup.string().nullable(),
          medicineDoseId: Yup.number().required(),
          value: Yup.number().required(),
          label: Yup.string().nullable(),
          displayOrder: Yup.number().required(),
          isDeleted: Yup.boolean().required(),
          mode: Yup.string().required(),
        }),
      )
      .nullable(),
    fileteredMedicineMedicineDoseColl: Yup.array()
      .of(
        Yup.object().shape({
          medicineDoseTitle: Yup.string().nullable(),
          medicineDoseId: Yup.number().required(),
          value: Yup.number().required(),
          label: Yup.string().nullable(),
          displayOrder: Yup.number().required(),
          isDeleted: Yup.boolean().required(),
          mode: Yup.string().required(),
        }),
      )
      .nullable(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initMedicineData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  const watchedMedicineMedicineDoseColl = useWatch({
    name: "medicineMedicineDoseColl",
    control: methods.control,
  });

  const filteredMedicineMedicineDoseColl =
    watchedMedicineMedicineDoseColl?.filter(
      (x) => x?.isDeleted === false && x.mode !== "D",
    ) || [];

  const strengthValue = useWatch({
    name: "strengthValue",
    control: methods.control,
  });

  const unitPerVolumeValue = useWatch({
    name: "unitPerVolumeValue",
    control: methods.control,
  });

  const packageAmountOfContent = useWatch({
    name: "packageAmountOfContent",
    control: methods.control,
  });

  // GET MEDICINE DETAIL
  const getMedicineDetail = async () => {
    let item: any;
    let params = new URLSearchParams();
    params.append(
      "medicineId",
      copyMedicineId > 0 ? copyMedicineId?.toString() : medicineId?.toString(),
    );
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());

    try {
      const res: any = await GetMedicineDetail(params);
      item = res.data.data;
      methods.reset({
        ...initMedicineData,
        medicineId: copyMedicineId > 0 ? 0 : item?.medicineId || 0,
        genericNameId: item?.genericNameId
          ? {
              label: item?.medicineGenericName || "",
              value: item?.genericNameId || 0,
            }
          : null,
        // genericName: item?.medicineGenericName || "",
        // brandName:
        //   (copyMedicineId && copyMedicineId > 0 ? "(1)" : "") +
        //     item?.brandName || "",
        // medicineCompanyName: item?.medicineCompanyName || "",
        // medicineCompanyId: item?.medicineCompanyId
        //   ? {
        //       label: item?.medicineCompanyName || "",
        //       value: item?.medicineCompanyId || 0,
        //     }
        //   : null,
        genericName: item?.medicineGenericName || "",
        brandName:
          (item?.brandName || "") +
          (copyMedicineId && copyMedicineId > 0 ? " (1)" : ""),
        medicineCompanyName: item?.medicineCompanyName || "",
        medicineCompanyId: item?.medicineCompanyId
          ? {
              label: item?.medicineCompanyName || "",
              value: item?.medicineCompanyId || 0,
            }
          : null,
        medicineGroupName: item?.medicineGroupName || "",
        doseDecidingContent: item?.doseDecidingContent || null,
        displayMedicineGenericName: item?.displayMedicineGenericName || null,
        medicineGroupId: item?.medicineGroupId
          ? {
              label: item?.medicineGroupName || "",
              value: item?.medicineGroupId || 0,
            }
          : null,
        medicineFormulationName: item?.medicineFormulationName || "",
        medicineFormulationId: item?.medicineFormulationId
          ? {
              label: item?.medicineFormulationName || "",
              value: item?.medicineFormulationId || 0,
            }
          : null,
        strengthValue: item?.strengthValue || 0,
        strengthUnitId: item?.strengthUnitId
          ? {
              label: item?.strengthUnitName || "",
              value: item?.strengthUnitId || "",
            }
          : null,
        strengthUnitName: item?.strengthUnitName || "",
        unitPerVolumeValue: item?.unitPerVolumeValue || 0,
        unitPerVolumeUnitId: item?.unitPerVolumeUnitId
          ? {
              label: item?.unitPerVolumeUnitName || "",
              value: item?.unitPerVolumeUnitId || "",
            }
          : null,
        unitPerVolumeUnitName: item?.unitPerVolumeUnitName || "",
        packageAmountOfContent: item?.packageAmountOfContent || 0,
        packageUnitPerVolume: item?.packageUnitPerVolume
          ? {
              label: item?.packageUnitPerVolumeName || "",
              value: item?.packageUnitPerVolume || "",
            }
          : null,
        packageUnitPerVolumeName: item?.packageUnitPerVolumeName,
        packageTypeName: item?.packageTypeName,
        packageType: item?.packageType
          ? packageTypes?.find((x) => x.value === item?.packageType)
          : null,
        dosePrintUnitName: item?.dosePrintUnitName || "",
        dosePrintUnit: item?.dosePrintUnit
          ? {
              label: item?.dosePrintUnitName?.toString() || "",
              value: item?.dosePrintUnit?.toString(),
            }
          : null,
        previousMedicineId: item?.previousMedicineId || 0,
        nextMedicineId: item?.nextMedicineId,
        medicineMedicineDoseColl: item?.medicineMedicineDoseColl || [],
        fileteredMedicineMedicineDoseColl: item?.medicineMedicineDoseColl || [],
      });
      return item;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // HANDLE COPY MEDICINE
  const handleCopyMedicine = (medicineId: any) => {
    let encryptmedicinetCopyId: any = edService.encrypt(medicineId);
    let encryptmedicinetId: any = edService.encrypt(0);
    navigate(
      {
        pathname: "/master/medicine/medicine-save",
        search: `?q=${encodeURIComponent(
          encryptmedicinetId,
        )}&&c=${encodeURIComponent(encryptmedicinetCopyId)}`,
      },
      {
        replace: true,
        state: {
          title: "Copy",
          button: "Save",
          medicineId: medicineId,
        },
      },
    );
  };

  // HANDLE PREVIOUS OR NEXT MEDICINE
  const handlePreviousOrNExtMedicine = (medicineId: any) => {
    let encryptmedicinetCopyId: any = edService.encrypt(medicineId);
    navigate(
      {
        pathname: "/master/medicine/medicine-save",
        search: `?q=${encodeURIComponent(encryptmedicinetCopyId)}`,
      },
      {
        replace: true,
      },
    );
  };

  // SAVE AND UPDATE BRANCH
  const saveMedicine = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        medicineId: value.medicineId,
        consumerId: userContext?.consumerId,
        orgId: userContext?.organizationId,
        branchId: userContext?.branchId,
        genericNameId: value.genericNameId?.value,
        genericName: value?.genericName?.trim() || "",
        brandName: value?.brandName?.trim() || "",
        medicineCompanyName: value?.medicineCompanyName?.trim() || "",
        medicineCompanyId: value.medicineCompanyId?.value,
        medicineGroupName: value?.medicineGroupName?.trim() || "",
        doseDecidingContent: value?.doseDecidingContent?.trim() || "",
        displayMedicineGenericName:
          value?.displayMedicineGenericName?.trim() || "",
        medicineGroupId: value.medicineGroupId?.value,
        medicineFormulationName: value?.medicineFormulationName?.trim() || "",
        medicineFormulationId: value.medicineFormulationId?.value,
        strengthValue: value.strengthValue || 0,
        strengthUnitId: value.strengthUnitId?.value,
        strengthUnitName: value.strengthUnitName,
        unitPerVolumeValue: value.unitPerVolumeValue || 0,
        unitPerVolumeUnitId: value.unitPerVolumeUnitId?.value || "",
        unitPerVolumeUnitName: value.unitPerVolumeUnitName,
        packageAmountOfContent: value.packageAmountOfContent || 0,
        packageUnitPerVolume: value.packageUnitPerVolume?.value || "",
        packageUnitPerVolumeName: value.packageUnitPerVolumeName || "",
        packageType: value.packageType?.value,
        packageTypeName: value?.packageTypeName || "",
        dosePrintUnit: value?.dosePrintUnit?.value,
        dosePrintUnitName: value?.dosePrintUnitName || "",
        medicineMedicineDoseCollJson: JSON.stringify(
          value?.medicineMedicineDoseColl || "",
        ),
        isDeleted: value.isDeleted,
        dataManagedBy: userContext.loginDetail.userId,
      };
      if (medicineId > 0) {
        result = await UpdateMedicine(formData);
      } else {
        result = await CreateMedicine(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          if (param.saveType === "SAVE_&_COPY") {
            handleCopyMedicine(result?.data?.medicineId || 0);
          } else if (
            ["SAVE_&_NEXT", "SAVE_&_PREVIOUS"].includes(param.saveType)
          ) {
            handlePreviousOrNExtMedicine(
              param.saveType === "SAVE_&_NEXT"
                ? value?.nextMedicineId
                : value?.previousMedicineId,
            );
          } else {
            navigate(-1);
          }
        } else {
          if (medicineId > 0) {
            const data = {
              MedicineId: result?.data?.medicineId,
              MedicineLabel: result?.data?.medicineLabel,
            };
            handleClosePopUp("MEDICINE_UPDATE", data);
          } else {
            const data = {
              MedicineId: result?.data?.medicineId,
              MedicineLabel: result?.data?.medicineLabel,
            };
            handleClosePopUp("MEDICINE_CREATE", data);
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveMedicine(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
      //  navigate("/master/medicine?page=1&q=");
    }
  };

  // GET COMPANY NAME
  async function fetchMedicineCompanyName(
    searchValue: string,
    oninit: boolean,
  ) {
    try {
      let medicineCompanyColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue.toString());
      params.append("mode", "DROPDOWN");
      const hsnSacData: any = await GetMedicineCompanyList(params);
      if (hsnSacData?.data?.data?.medicineCompanyColl) {
        medicineCompanyColl = hsnSacData.data.data.medicineCompanyColl;
        if (oninit) setMedicineCompanyNameData(medicineCompanyColl);
      }
      return medicineCompanyColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER COMPANY NAME
  async function filterMedicineCompanyName(inputValue: string) {
    let result = await fetchMedicineCompanyName(inputValue, false);
    return result;
  }

  // COMPANY NAME LOAD ON SEARCH
  const loadMedicineCompanyNameList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicineCompanyName(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET GENERIC NAME
  async function fetchMedicineGenericName(
    searchValue: string,
    oninit: boolean,
  ) {
    try {
      let medicineGenericNameColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue.toString());
      params.append("mode", "DROPDOWN");
      const hsnSacData: any = await GetMedicineGenericNameList(params);
      if (hsnSacData?.data?.data?.medicineGenericNameColl) {
        medicineGenericNameColl = hsnSacData.data.data.medicineGenericNameColl;
        if (oninit) setMedicineGenericNameData(medicineGenericNameColl);
      }
      return medicineGenericNameColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER GENERIC NAME
  async function filterMedicineGenericName(inputValue: string) {
    let result = await fetchMedicineGenericName(inputValue, false);
    return result;
  }

  // GENERIC NAME LOAD ON SEARCH
  const loadMedicineGenericNameList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicineGenericName(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET MEDICINE GROUP
  async function fetchMedicineGroup(searchValue: string, oninit: boolean) {
    try {
      let medicineGroupColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue.toString());
      params.append("mode", "DROPDOWN");
      const hsnSacData: any = await GetMedicineGroupList(params);
      if (hsnSacData?.data?.data?.medicineGroupColl) {
        medicineGroupColl = hsnSacData.data.data.medicineGroupColl;
        if (oninit) setMedicineGroupData(medicineGroupColl);
      }
      return medicineGroupColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER MEDICINE GROUP
  async function filterMedicineGroup(inputValue: string) {
    let result = await fetchMedicineGroup(inputValue, false);
    return result;
  }

  // MEDICINE GROUP LOAD ON SEARCH
  const loadMedicineGroupList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicineGroup(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET MEDICINE FORMULATION
  async function fetchMedicineFormulation(
    searchValue: string,
    oninit: boolean,
  ) {
    try {
      let medicineFormulationColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue.toString());
      params.append("mode", "DROPDOWN");
      const hsnSacData: any = await GetMedicineFormulationList(params);
      if (hsnSacData?.data?.data?.medicineFormulationColl) {
        medicineFormulationColl = hsnSacData.data.data.medicineFormulationColl;
        if (oninit) setMedicineFormulationData(medicineFormulationColl);
      }
      return medicineFormulationColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FILTER MEDICINE FORMULATION
  async function filterMedicineFormulation(inputValue: string) {
    let result = await fetchMedicineFormulation(inputValue, false);
    return result;
  }

  // MEDICINE FORMULATION   LOAD ON SEARCH
  const loadMedicineFormulationList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicineFormulation(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET  STRENGTH UNITS
  async function fetchStrengthUnitList() {
    try {
      let strengthUnitColl = [];
      const result: any = await GetStrengthUnitList();
      if (result?.data?.data) {
        strengthUnitColl = result?.data?.data;
        updateUserContext({
          ...userContext,
          strengthUnitColl: strengthUnitColl,
        });
        setStrengthUnitColl(strengthUnitColl);
      }
      return strengthUnitColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET  PER VOLUME   UNITS
  async function fetchPerVolumeUnitList() {
    try {
      let perVolumeUnitColl = [];
      const result: any = await GetPerVolumeUnitList();
      if (result?.data?.data) {
        perVolumeUnitColl = result?.data?.data;
        updateUserContext({
          ...userContext,
          perVolumeUnitColl: perVolumeUnitColl,
        });
        setPerVolumeUnitColl(perVolumeUnitColl);
      }
      return perVolumeUnitColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET PRINT UNITS
  async function fetchPrintUnitList() {
    try {
      let printUnitColl = [];
      const result: any = await GetPrintUnitList();
      if (result?.data?.data) {
        printUnitColl = result?.data?.data;
        updateUserContext({
          ...userContext,
          printUnitColl: printUnitColl,
        });
        setPrintUnitColl(printUnitColl);
      }
      return printUnitColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET  PER VOLUME   UNITS
  async function fetchPackagePerVolumeUnitList() {
    try {
      let packagePerVolumeUnitColl = [];
      const result: any = await GetPackagePerVolumeUnitList();
      if (result?.data?.data) {
        packagePerVolumeUnitColl = result?.data?.data;
        updateUserContext({
          ...userContext,
          packagePerVolumeUnitColl: packagePerVolumeUnitColl,
        });
        setPackagePerVolumeUnitColl(packagePerVolumeUnitColl);
      }
      return packagePerVolumeUnitColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET  PACKAGE UNIT LIST
  async function fetchPackageUnitList() {
    try {
      let packageUnitColl = [];
      const result: any = await GetPackageUnitList();
      if (result?.data?.data) {
        packageUnitColl = result?.data?.data;
        updateUserContext({
          ...userContext,
          packageUnitColl: packageUnitColl,
        });
        setPackageUnitColl(packageUnitColl);
      }
      return packageUnitColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET MEDICINE DOSE
  const fetchMedicineDose = async (
    genericNameId: string,
    searchValue: string,
    oninit: boolean,
  ) => {
    try {
      let medicineDoseColl = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("genericNameId", genericNameId);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue || "");
      const result: any = await GetMedicineDoseList(params);
      if (result?.data?.data?.medicineDoseColl) {
        medicineDoseColl = result.data.data.medicineDoseColl;
        if (oninit) setMedicineDoseColl(medicineDoseColl);
      }
      return medicineDoseColl;
    } catch (e) {
      console.log("err:", e);
    }
  };

  async function filterMedicineDose(genericNameId: string, inputValue: string) {
    let result = await fetchMedicineDose(genericNameId, inputValue, false);
    return result;
  }

  // MEDICINE DOSE LOAD ON SEARCH
  const loadMedicineDoseList = (
    genericNameId: string,
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterMedicineDose(genericNameId, inputValue).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  const handleDeleteMedicineDose = (
    mode: string,
    medicineDose?: IMedicineMedicineDose,
  ) => {
    if (mode === "ALL") {
      let detailColl =
        watchedMedicineMedicineDoseColl?.map((item) => {
          item.isDeleted = true;
          item.mode = "D";
          return item;
        }) || [];

      methods.setValue("medicineMedicineDoseColl", detailColl);
      methods.setValue("fileteredMedicineMedicineDoseColl", []);
    } else if (mode === "ROW" && medicineDose) {
      let detailColl = watchedMedicineMedicineDoseColl || [];
      detailColl[medicineDose.displayOrder].isDeleted = true;
      detailColl[medicineDose.displayOrder].mode = "D";

      methods.setValue("medicineMedicineDoseColl", detailColl);
      let filteredDetailColl =
        detailColl?.filter((x) => x?.isDeleted === false && x.mode !== "D") ||
        [];
      methods.setValue("fileteredMedicineMedicineDoseColl", filteredDetailColl);
    }
  };

  const handleMedicineDoseAdd = (value: any) => {
    const detailColl = watchedMedicineMedicineDoseColl || [];

    const maxRowNumber = detailColl?.reduce(
      (acc, curr) => (acc = curr.displayOrder > acc ? curr.displayOrder : acc),
      0,
    );

    const newRow: IMedicineMedicineDose = {
      displayOrder: detailColl.length === 0 ? 0 : maxRowNumber + 1,
      medicineDoseId: value?.value || 0,
      label: value?.label || "",
      value: value?.value,
      isDeleted: false,
      mode: "I",
      medicineDoseTitle: value?.medicineDoseTitle || "",
      medicineGenericName: value?.medicineGenericName || "",
      weightBsaDependentDesiredDoseValue:
        value?.weightBsaDependentDesiredDoseValue || 0,
      weightBsaDependentMinDoseValue:
        value?.weightBsaDependentMinDoseValue || 0,
      weightBsaDependentDesiredDoseUnitName:
        value?.weightBsaDependentDesiredDoseUnitName || "",
      weightBsaDependentPerWeightBsaUnitName:
        value?.weightBsaDependentPerWeightBsaUnitName || "",
      weightBsaDependentPerIntervalUnitName:
        value?.weightBsaDependentPerIntervalUnitName || "",
      weightBsaDependentMaxDoseValue:
        value?.weightBsaDependentMaxDoseValue || 0,
      fixedDoseValue: value?.fixedDoseValue || 0,
      maxFixedDoseValue: value?.maxFixedDoseValue || 0,
      fixedDoseUnitName: value?.fixedDoseUnitName || "",
      fixedDosePerIntervalUnitName: value?.fixedDosePerIntervalUnitName || "",
      maxFixedDoseUnitName: value?.maxFixedDoseUnitName || "",
      maxFixedDosePerIntervalUnitName:
        value?.maxFixedDosePerIntervalUnitName || "",
    };

    methods.setValue("medicineMedicineDoseColl", [...detailColl, newRow], {
      shouldValidate: true,
    });

    methods.setValue(
      "fileteredMedicineMedicineDoseColl",
      [...filteredMedicineMedicineDoseColl, newRow],
      {
        shouldValidate: true,
      },
    );
  };

  const handleAddAllMedicineDose = (options) => {
    const detailColl = watchedMedicineMedicineDoseColl || [];

    const maxRowNumber = detailColl?.reduce(
      (acc, curr) => (acc = curr.displayOrder > acc ? curr.displayOrder : acc),
      0,
    );

    const existingMedicineDoseIds = new Set(
      detailColl.map((item) => item.value),
    );

    let newDetailColl = options?.reduce((acc, curr, index) => {
      if (!curr?.__isNew__ && !existingMedicineDoseIds.has(curr?.value)) {
        const newRow: IMedicineMedicineDose = {
          displayOrder:
            detailColl.length === 0 ? index : index + maxRowNumber + 1,
          label: curr?.label || "",
          value: curr?.value,
          isDeleted: false,
          mode: "I",
          medicineDoseTitle: curr?.label || "",
          medicineDoseId: curr?.value || 0,
          medicineGenericName: curr?.medicineGenericName || "",
          weightBsaDependentDesiredDoseValue:
            curr?.weightBsaDependentDesiredDoseValue || 0,
          weightBsaDependentMinDoseValue:
            curr?.weightBsaDependentMinDoseValue || 0,
          weightBsaDependentDesiredDoseUnitName:
            curr?.weightBsaDependentDesiredDoseUnitName || "",
          weightBsaDependentPerWeightBsaUnitName:
            curr?.weightBsaDependentPerWeightBsaUnitName || "",
          weightBsaDependentPerIntervalUnitName:
            curr?.weightBsaDependentPerIntervalUnitName || "",
          weightBsaDependentMaxDoseValue:
            curr?.weightBsaDependentMaxDoseValue || 0,
          fixedDoseValue: curr?.fixedDoseValue || 0,
          maxFixedDoseValue: curr?.maxFixedDoseValue || 0,
          fixedDoseUnitName: curr?.fixedDoseUnitName || "",
          fixedDosePerIntervalUnitName:
            curr?.fixedDosePerIntervalUnitName || "",
          maxFixedDoseUnitName: curr?.maxFixedDoseUnitName || "",
          maxFixedDosePerIntervalUnitName:
            curr?.maxFixedDosePerIntervalUnitName || "",
        };
        acc = [...acc, newRow];
      }
      return acc;
    }, []);

    methods.setValue(
      "medicineMedicineDoseColl",
      [...detailColl, ...newDetailColl],
      {
        shouldValidate: true,
      },
    );

    methods.setValue("fileteredMedicineMedicineDoseColl", [
      ...filteredMedicineMedicineDoseColl,
      ...newDetailColl,
    ]);
  };

  const medicineDoseOptionComponent = (props) => {
    return (
      <components.Option {...props}>
        {props.data.__isNew__ && <Box>{props.children}</Box>}
        {props.data && !props.data.__isNew__ && (
          <Box>
            <Grid container spacing={1}>
              <Grid item xs={12} md={8}>
                <Typography>{props?.children}</Typography>
                <Typography className="font-size-12 grey-400">
                  {props?.data?.medicineGenericName || ""}
                </Typography>
                <Box
                  sx={{
                    display: mdd ? "flex" : "none",
                    gap: 2,
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography className="grey-400">
                      {" "}
                      {props?.data?.weightBsaDependentDesiredDoseValue > 0 ||
                      (props?.data?.weightBsaDependentMinDoseValue ?? 0) > 0
                        ? "Weight/BSA Dose: "
                        : ""}{" "}
                    </Typography>
                    <Typography className="font-size-14 ">
                      {" "}
                      {props?.data?.weightBsaDependentDesiredDoseValue > 0 && (
                        <>
                          {props?.data?.weightBsaDependentDesiredDoseValue?.toString() +
                            " " +
                            props?.data?.weightBsaDependentDesiredDoseUnitName +
                            "/" +
                            props?.data
                              ?.weightBsaDependentPerWeightBsaUnitName +
                            "/" +
                            props?.data?.weightBsaDependentPerIntervalUnitName}
                        </>
                      )}
                    </Typography>
                    <Typography className="font-size-14 ">
                      {(props?.data?.weightBsaDependentMinDoseValue ?? 0) > 0 &&
                        "Min: " + props?.data?.weightBsaDependentMinDoseValue}

                      {props?.data?.weightBsaDependentMinDoseValue > 0 &&
                        props?.data?.weightBsaDependentMaxDoseValue > 0 &&
                        " - "}

                      {props?.data?.weightBsaDependentMaxDoseValue > 0 &&
                        "Max: " + props?.data?.weightBsaDependentMaxDoseValue}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography className="grey-400">
                      {props?.data?.fixedDoseValue > 0 ||
                      props?.data?.maxFixedDoseValue > 0
                        ? "Fixed Dose: "
                        : ""}
                    </Typography>
                    <Typography className="font-size-14 ">
                      {" "}
                      {props?.data?.fixedDoseValue > 0 &&
                        props?.data?.fixedDoseValue +
                          " " +
                          props?.data?.fixedDoseUnitName +
                          "/" +
                          props?.data?.fixedDosePerIntervalUnitName}
                    </Typography>
                    <Typography className="font-size-14 ">
                      {props?.data?.maxFixedDoseValue > 0 &&
                        "Max: " +
                          props?.data?.maxFixedDoseValue +
                          " " +
                          props?.data?.maxFixedDoseUnitName +
                          "/" +
                          props?.data?.maxFixedDosePerIntervalUnitName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid
                item
                md={2}
                display={{
                  xs: "none",
                  md: "grid",
                }}
              >
                <Typography className="font-size-14 ">
                  {" "}
                  {props?.data?.weightBsaDependentDesiredDoseValue > 0 && (
                    <>
                      {props?.data?.weightBsaDependentDesiredDoseValue?.toString() +
                        " " +
                        props?.data?.weightBsaDependentDesiredDoseUnitName +
                        "/" +
                        props?.data?.weightBsaDependentPerWeightBsaUnitName +
                        "/" +
                        props?.data?.weightBsaDependentPerIntervalUnitName}
                    </>
                  )}
                </Typography>
                <Typography className="font-size-14 ">
                  {(props?.data?.weightBsaDependentMinDoseValue ?? 0) > 0 &&
                    "Min: " + props?.data?.weightBsaDependentMinDoseValue}

                  {props?.data?.weightBsaDependentMinDoseValue > 0 &&
                    props?.data?.weightBsaDependentMaxDoseValue > 0 &&
                    " - "}

                  {props?.data?.weightBsaDependentMaxDoseValue > 0 &&
                    "Max: " + props?.data?.weightBsaDependentMaxDoseValue}
                </Typography>
              </Grid>
              <Grid
                item
                md={2}
                display={{
                  xs: "none",
                  md: "grid",
                }}
              >
                <Typography className="font-size-14 ">
                  {" "}
                  {props?.data?.fixedDoseValue > 0 &&
                    props?.data?.fixedDoseValue +
                      " " +
                      props?.data?.fixedDoseUnitName +
                      "/" +
                      props?.data?.fixedDosePerIntervalUnitName}
                </Typography>
                <Typography className="font-size-14 ">
                  {props?.data?.maxFixedDoseValue > 0 &&
                    "Max: " +
                      props?.data?.maxFixedDoseValue +
                      " " +
                      props?.data?.maxFixedDoseUnitName +
                      "/" +
                      props?.data?.maxFixedDosePerIntervalUnitName}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </components.Option>
    );
  };

  const medicineDoseMenuListComponent = (props) => {
    let optionsLength = 0;
    if (Array.isArray(props?.children)) {
      optionsLength =
        props?.children?.filter((item) => !item?.props?.data?.__isNew__)
          ?.length || 0;
    }
    return (
      <components.MenuList {...props}>
        {optionsLength > 0 && (
          <>
            <Box
              sx={{
                px: 2,
                py: 1,
              }}
            >
              <Tooltip
                title={"This will add all searched options"}
                placement="right"
              >
                <Typography
                  sx={{ width: "max-content" }}
                  className="text-link link-cursor"
                  onClick={() => {
                    if (props?.options?.length > 0) {
                      handleAddAllMedicineDose(props.options);
                    }
                  }}
                >
                  ALL
                </Typography>
              </Tooltip>
            </Box>
            <Box
              sx={{
                px: 2,
                py: 1,
                display: mdd ? "none" : "block",
              }}
              className="medicine-dose-menu-list-header"
            >
              <Grid container spacing={1}>
                <Grid item xs={8}>
                  <Typography className="bold-600">Medicine Dose</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography className="bold-600">Weight/BSA Dose</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography className="bold-600">Fixed Dose</Typography>
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        {props.children}
      </components.MenuList>
    );
  };

  const MedicineDoseCard = ({ item }: { item: IMedicineMedicineDose }) => {
    return (
      <Box
        sx={{ display: "flex", gap: 2, padding: "10px", alignItems: "center" }}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} md={8}>
            <Typography
              className="text-link link-cursor"
              onClick={() => {
                handleClickOpen(
                  true,
                  item?.label || "",
                  "MEDICINE_DOSE",
                  null,
                  item?.value || 0,
                );
              }}
            >
              {item?.medicineDoseTitle || ""}
            </Typography>
            <Typography className="font-size-12 grey-400">
              {item?.medicineGenericName || ""}
            </Typography>
            <Box
              sx={{
                display: mdd ? "flex" : "none",
                gap: 2,
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography className="grey-400">
                  {" "}
                  {item?.weightBsaDependentDesiredDoseValue > 0 ||
                  (item?.weightBsaDependentMinDoseValue ?? 0) > 0
                    ? "Weight/BSA Dose: "
                    : ""}{" "}
                </Typography>
                <Typography className="font-size-14 ">
                  {" "}
                  {item?.weightBsaDependentDesiredDoseValue > 0 && (
                    <>
                      {item?.weightBsaDependentDesiredDoseValue?.toString() +
                        " " +
                        item?.weightBsaDependentDesiredDoseUnitName +
                        "/" +
                        item?.weightBsaDependentPerWeightBsaUnitName +
                        "/" +
                        item?.weightBsaDependentPerIntervalUnitName}
                    </>
                  )}
                </Typography>
                <Typography className="font-size-14 ">
                  {(item?.weightBsaDependentMinDoseValue ?? 0) > 0 &&
                    "Min: " + item?.weightBsaDependentMinDoseValue}

                  {item?.weightBsaDependentMinDoseValue > 0 &&
                    item?.weightBsaDependentMaxDoseValue > 0 &&
                    " - "}

                  {item?.weightBsaDependentMaxDoseValue > 0 &&
                    "Max: " + item?.weightBsaDependentMaxDoseValue}
                </Typography>
              </Box>
              <Box>
                <Typography className="grey-400">
                  {item?.fixedDoseValue > 0 || item?.maxFixedDoseValue > 0
                    ? "Fixed Dose: "
                    : ""}
                </Typography>
                <Typography className="font-size-14 ">
                  {" "}
                  {item?.fixedDoseValue > 0 &&
                    item?.fixedDoseValue +
                      " " +
                      item?.fixedDoseUnitName +
                      "/" +
                      item?.fixedDosePerIntervalUnitName}
                </Typography>
                <Typography className="font-size-14 ">
                  {item?.maxFixedDoseValue > 0 &&
                    "Max: " +
                      item?.maxFixedDoseValue +
                      " " +
                      item?.maxFixedDoseUnitName +
                      "/" +
                      item?.maxFixedDosePerIntervalUnitName}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            md={2}
            display={{
              xs: "none",
              md: "grid",
            }}
          >
            <Typography className="font-size-14 ">
              {" "}
              {item?.weightBsaDependentDesiredDoseValue > 0 && (
                <>
                  {item?.weightBsaDependentDesiredDoseValue?.toString() +
                    " " +
                    item?.weightBsaDependentDesiredDoseUnitName +
                    "/" +
                    item?.weightBsaDependentPerWeightBsaUnitName +
                    "/" +
                    item?.weightBsaDependentPerIntervalUnitName}
                </>
              )}
            </Typography>
            <Typography className="font-size-14 ">
              {(item?.weightBsaDependentMinDoseValue ?? 0) > 0 &&
                "Min: " + item?.weightBsaDependentMinDoseValue}

              {item?.weightBsaDependentMinDoseValue > 0 &&
                item?.weightBsaDependentMaxDoseValue > 0 &&
                " - "}

              {item?.weightBsaDependentMaxDoseValue > 0 &&
                "Max: " + item?.weightBsaDependentMaxDoseValue}
            </Typography>
          </Grid>
          <Grid
            item
            md={2}
            display={{
              xs: "none",
              md: "grid",
            }}
          >
            <Typography className="font-size-14 ">
              {" "}
              {item?.fixedDoseValue > 0 &&
                item?.fixedDoseValue +
                  " " +
                  item?.fixedDoseUnitName +
                  "/" +
                  item?.fixedDosePerIntervalUnitName}
            </Typography>
            <Typography className="font-size-14 ">
              {item?.maxFixedDoseValue > 0 &&
                "Max: " +
                  item?.maxFixedDoseValue +
                  " " +
                  item?.maxFixedDoseUnitName +
                  "/" +
                  item?.maxFixedDosePerIntervalUnitName}
            </Typography>
          </Grid>
        </Grid>
        <CloseRoundedIcon
          onClick={() => {
            handleDeleteMedicineDose("ROW", item);
          }}
          style={{ color: "#d32f2f", cursor: "pointer" }}
        />
      </Box>
    );
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      await fetchMedicineGenericName("", true);
      await fetchMedicineCompanyName("", true);
      await fetchMedicineGroup("", true);
      await fetchMedicineFormulation("", true);

      if (strengthUnitColl?.length <= 0) {
        await fetchStrengthUnitList();
      }
      if (perVolumeUnitColl?.length <= 0) {
        await fetchPerVolumeUnitList();
      }
      if (printUnitColl?.length <= 0) {
        await fetchPrintUnitList();
      }
      if (packagePerVolumeUnitColl?.length <= 0) {
        await fetchPackagePerVolumeUnitList();
      }
      if (packageUnitColl?.length <= 0) {
        await fetchPackageUnitList();
      }
      if (medicineId > 0 || copyMedicineId > 0) {
        const medicine = await getMedicineDetail();
        if (medicine?.genericNameId > 0) {
          await fetchMedicineDose(medicine?.genericNameId || "0", "", true);
        }
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [medicineId, copyMedicineId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <>
            <Grid container className="page-header">
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignMedicines: "center",
                  }}
                >
                  <CustomBreadcrumb />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Basic Information</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.brandName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Brand Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          id="brandName"
                          size="small"
                          autoFocus
                          {...methods.register("brandName", {
                            onChange: (e) => {
                              const input = e.target;
                              const selectionStart = input.selectionStart;
                              const selectionEnd = input.selectionEnd;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";

                              // Restore the cursor position
                              setTimeout(() => {
                                input.setSelectionRange(
                                  selectionStart,
                                  selectionEnd,
                                );
                              });

                              methods.setValue("brandName", e.target.value, {
                                shouldValidate: true,
                              }); // UPDATE VALUE
                            },
                          })}
                          error={!!methods.formState.errors.brandName}
                          className={`text-input ${
                            methods.formState.errors.brandName ? "error" : ""
                          }`}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.brandName?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.genericNameId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Dose deciding content/Generic name{" "}
                        <CustomTooltip
                          title="This field is important for better recored management"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="genericNameId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              id={"genericNameId"}
                              FieldName={"genericNameId"}
                              defaultOptions={medicineGenericNameData}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              isDisabled={
                                genericNameObject
                                // ||
                                // medicineId > 0 ||
                                // popUpMedicineId > 0
                              }
                              loadOptions={loadMedicineGenericNameList}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (e?.__isNew__ === true) {
                                  handleClickOpen(
                                    true,
                                    e.label,
                                    "MEDICINE_GENERIC",
                                  );
                                } else if (
                                  methods
                                    ?.getValues()
                                    ?.genericNameId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "genericNameId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue(
                                    "genericName",
                                    e?.value ? e?.label : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  handleDeleteMedicineDose("ALL");
                                  setIsLoading(true);
                                  if (e?.value) {
                                    await fetchMedicineDose(e?.value, "", true);
                                  } else {
                                    setMedicineDoseColl([]);
                                  }
                                  setIsLoading(false);
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.genericNameId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.displayMedicineGenericName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Display Contents{" "}
                        <CustomTooltip
                          title="This field is important for better recored management"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          // placeholder="Ex. PCM for Fever or PCM for Pain"
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          id="displayMedicineGenericName"
                          size="small"
                          autoFocus
                          {...methods.register("displayMedicineGenericName", {
                            onChange: (e) => {
                              const input = e.target;
                              const selectionStart = input.selectionStart;
                              const selectionEnd = input.selectionEnd;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";

                              // Restore the cursor position
                              setTimeout(() => {
                                input.setSelectionRange(
                                  selectionStart,
                                  selectionEnd,
                                );
                              });

                              methods.setValue(
                                "displayMedicineGenericName",
                                e.target.value,
                                {
                                  shouldValidate: true,
                                },
                              ); // UPDATE VALUE
                            },
                          })}
                          error={
                            !!methods.formState.errors
                              .displayMedicineGenericName
                          }
                          className={`text-input ${
                            methods.formState.errors.displayMedicineGenericName
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {
                          methods.formState.errors.displayMedicineGenericName
                            ?.message
                        }
                      </Typography>
                    </FormControl>
                  </Grid>
                  {/* <Grid item xs={12} sm={6} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.doseDecidingContent
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Dose deciding content/Generic name
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          id="doseDecidingContent"
                          size="small"
                          autoFocus
                          {...methods.register("doseDecidingContent", {
                            onChange: (e) => {
                              const input = e.target;
                              const selectionStart = input.selectionStart;
                              const selectionEnd = input.selectionEnd;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";

                              // Restore the cursor position
                              setTimeout(() => {
                                input.setSelectionRange(
                                  selectionStart,
                                  selectionEnd
                                );
                              });

                              methods.setValue(
                                "doseDecidingContent",
                                e.target.value,
                                {
                                  shouldValidate: true,
                                }
                              ); // UPDATE VALUE
                            },
                          })}
                          error={!!methods.formState.errors.doseDecidingContent}
                          className={`text-input ${
                            methods.formState.errors.doseDecidingContent
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.doseDecidingContent?.message}
                      </Typography>
                    </FormControl>
                  </Grid> */}
                  <Grid item xs={12} sm={6} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicineCompanyId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Company / Marketer Name
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="medicineCompanyId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              id={"medicineCompanyId"}
                              FieldName={"medicineCompanyId"}
                              defaultOptions={medicineCompanyNameData}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              loadOptions={loadMedicineCompanyNameList}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (e?.__isNew__ === true) {
                                  handleClickOpen(
                                    true,
                                    e.label,
                                    "MEDICINE_COMPANY",
                                  );
                                } else if (
                                  methods
                                    ?.getValues()
                                    ?.medicineCompanyId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "medicineCompanyId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue(
                                    "medicineCompanyName",
                                    e?.value ? e?.label : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.medicineCompanyId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    md={6}
                    sx={{
                      display: {
                        xs: "none",
                        md: "grid",
                        lg: "none",
                      },
                    }}
                  ></Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicineGroupId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Medicine Group
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="medicineGroupId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              id={"medicineGroupId"}
                              FieldName={"medicineGroupId"}
                              defaultOptions={medicineGroupData}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              loadOptions={loadMedicineGroupList}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (e?.__isNew__ === true) {
                                  handleClickOpen(
                                    true,
                                    e.label,
                                    "MEDICINE_GROUP",
                                  );
                                } else if (
                                  methods
                                    ?.getValues()
                                    ?.medicineGroupId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "medicineGroupId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue(
                                    "medicineGroupName",
                                    e?.value ? e?.label : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.medicineGroupId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicineFormulationId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Formulation{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="medicineFormulationId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              id={"medicineFormulationId"}
                              FieldName={"medicineFormulationId"}
                              defaultOptions={medicineFormulationData}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              loadOptions={loadMedicineFormulationList}
                              handleChange={async (e) => {
                                if (e?.__isNew__ === true) {
                                  handleClickOpen(
                                    true,
                                    e.label,
                                    "MEDICINE_FORMULATION",
                                  );
                                } else if (
                                  methods
                                    ?.getValues()
                                    ?.medicineFormulationId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "medicineFormulationId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue(
                                    "medicineFormulationName",
                                    e?.value ? e?.label : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {
                          methods.formState.errors.medicineFormulationId
                            ?.message
                        }
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    md={6}
                    display={{
                      xs: "none",
                      md: "grid",
                    }}
                  ></Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.strengthValue
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Strength of dose deciding Content{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0.0000"
                          fullWidth
                          id="strengthValue"
                          size="small"
                          onFocus={handleFocus}
                          inputProps={numberProps}
                          {...methods.register("strengthValue")}
                          error={!!methods.formState.errors.strengthValue}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  className="input-end-andorment-border"
                                  sx={{
                                    minWidth: "100px",
                                  }}
                                >
                                  <CustomSelect
                                    openMenuOnFocus
                                    value={methods.getValues("strengthUnitId")}
                                    menuPortalTarget={document.querySelector(
                                      "body",
                                    )}
                                    handleChange={(e) => {
                                      methods.setValue(
                                        "strengthUnitId",
                                        e || null,
                                        { shouldValidate: true },
                                      );
                                      methods.setValue(
                                        "strengthUnitName",
                                        e?.label || null,
                                        { shouldValidate: true },
                                      );
                                    }}
                                    options={strengthUnitColl}
                                    isNotCreatable={true}
                                    isIndicatorSeparator={false}
                                    isBorderNone
                                    id={"strengthUnitId"}
                                    FieldName={"strengthUnitId"}
                                  />
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.strengthValue?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.unitPerVolumeValue
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Per Unit/Volume{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0.0000"
                          fullWidth
                          id="unitPerVolumeValue"
                          size="small"
                          onFocus={handleFocus}
                          inputProps={numberProps}
                          {...methods.register("unitPerVolumeValue")}
                          error={!!methods.formState.errors.unitPerVolumeValue}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  className="input-end-andorment-border"
                                  sx={{
                                    minWidth: "140px",
                                  }}
                                >
                                  <CustomSelect
                                    openMenuOnFocus
                                    value={methods.getValues(
                                      "unitPerVolumeUnitId",
                                    )}
                                    menuPortalTarget={document.querySelector(
                                      "body",
                                    )}
                                    handleChange={(e) => {
                                      methods.setValue(
                                        "unitPerVolumeUnitId",
                                        e || null,
                                        { shouldValidate: true },
                                      );
                                      methods.setValue(
                                        "unitPerVolumeUnitName",
                                        e?.label || null,
                                        { shouldValidate: true },
                                      );
                                    }}
                                    options={perVolumeUnitColl}
                                    isNotCreatable={true}
                                    isIndicatorSeparator={false}
                                    isBorderNone
                                    id={"unitPerVolumeUnitId"}
                                    FieldName={"unitPerVolumeUnitId"}
                                  />
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.unitPerVolumeValue?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.dosePrintUnit
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Print unit{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="dosePrintUnit"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"dosePrintUnit"}
                              FieldName={"dosePrintUnit"}
                              options={printUnitColl}
                              openMenuOnFocus
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                methods.setValue(
                                  "dosePrintUnit",
                                  e !== null ? e : null,
                                  { shouldValidate: true },
                                );
                                methods.setValue(
                                  "dosePrintUnitName",
                                  e !== null ? e?.label : null,
                                  { shouldValidate: true },
                                );
                              }}
                              className={`text-input ${
                                methods.formState.errors.dosePrintUnit
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.dosePrintUnit?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography className="">
                      {strengthValue || 0}{" "}
                      {methods.getValues().strengthUnitName || ""} in{" "}
                      {unitPerVolumeValue || 0}{" "}
                      {methods.getValues()?.unitPerVolumeUnitName || ""}{" "}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Package Detail</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.packageAmountOfContent
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Amount of content{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0.0000"
                          fullWidth
                          id="packageAmountOfContent"
                          size="small"
                          onFocus={handleFocus}
                          disabled={
                            methods.getValues("packageUnitPerVolume")?.value ===
                            "UNIT"
                          }
                          inputProps={numberProps}
                          {...methods.register("packageAmountOfContent")}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  className="input-end-andorment-border"
                                  sx={{
                                    minWidth: "100px",
                                  }}
                                >
                                  <CustomSelect
                                    openMenuOnFocus
                                    value={methods.getValues(
                                      "packageUnitPerVolume",
                                    )}
                                    menuPortalTarget={document.querySelector(
                                      "body",
                                    )}
                                    handleChange={(e) => {
                                      methods.setValue(
                                        "packageUnitPerVolume",
                                        e || null,
                                        { shouldValidate: true },
                                      );
                                      methods.setValue(
                                        "packageUnitPerVolumeName",
                                        e?.label || null,
                                        { shouldValidate: true },
                                      );
                                      if (e?.value === "UNIT") {
                                        methods.setValue(
                                          "packageAmountOfContent",
                                          1,
                                          { shouldValidate: true },
                                        );
                                      }
                                    }}
                                    options={packagePerVolumeUnitColl}
                                    isNotCreatable={true}
                                    isIndicatorSeparator={false}
                                    isBorderNone
                                    id={"packageUnitPerVolumeName"}
                                    FieldName={"packageUnitPerVolumeName"}
                                  />
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                          error={
                            !!methods.formState.errors.packageAmountOfContent
                          }
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.packageAmountOfContent?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.packageType
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Package type{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="packageType"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"packageType"}
                              FieldName={"packageType"}
                              options={packageUnitColl}
                              openMenuOnFocus
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.packageType?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "packageType",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue(
                                    "packageTypeName",
                                    e?.value ? e?.label : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.packageType?.message}
                      </Typography>
                    </FormControl>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    sx={{ marginLeft: "30px" }}
                  >
                    <FormControl fullWidth disabled>
                      <FormLabel
                        className={"fileds-label-text"}
                        sx={{ marginTop: "10px" }}
                      >
                        Examples
                      </FormLabel>
                      <Typography>
                        60 ml Bottle, 1 unit Tablet, 1 unit Sachet
                      </Typography>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className="">
                      {packageAmountOfContent || 0}{" "}
                      {methods.getValues().packageUnitPerVolumeName || ""}{" "}
                      {methods.getValues().packageTypeName || ""}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {isFrom !== "MEDICINE_DOSE" && (
                <Grid item xs={12} className="group-container">
                  <Grid container item spacing={2}>
                    <Grid
                      item
                      xs={12}
                      md={12}
                      lg={isForPopUp ? 12 : 11}
                      xl={isForPopUp ? 12 : 9}
                    >
                      <Box
                        className="template-table-container"
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          border: "1px solid #0000001f",
                          borderRadius: 4,
                        }}
                      >
                        <Box className="inner-title-container">
                          <Box
                            className="template-box-sm"
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              backgroundColor: "#f1f5f8",
                            }}
                          >
                            <Grid container spacing={1}>
                              <Grid item xs={12} md={8}>
                                <Typography>Medicine Dose</Typography>
                              </Grid>
                              <Grid
                                item
                                md={2}
                                display={{
                                  xs: "none",
                                  md: "grid",
                                }}
                              >
                                <Typography>Weight/BSA Dose</Typography>
                              </Grid>
                              <Grid
                                item
                                md={2}
                                display={{
                                  xs: "none",
                                  md: "grid",
                                }}
                              >
                                <Typography>Fixed Dose</Typography>
                              </Grid>
                            </Grid>
                            <Typography
                              className="text-link-danger link-cursor"
                              onClick={(e) => {
                                handleDeleteMedicineDose("ALL");
                              }}
                            >
                              CLEAR
                            </Typography>
                          </Box>
                          <DraggableList
                            items={filteredMedicineMedicineDoseColl || []}
                            setItem={(e) => {
                              console.log(e);
                            }}
                            Styledcard={MedicineDoseCard}
                            className="draggable-list-container-scrollable"
                          />
                          <Box
                            style={{
                              padding: 10,
                              paddingLeft: 20,
                              paddingRight: 20,
                            }}
                          >
                            {methods.getValues("genericNameId") &&
                            Number(methods.getValues("genericNameId")?.value) >
                              0 ? (
                              <FormControl fullWidth>
                                <SkeletonWrapper
                                  isLoading={showSkeleton}
                                  className="skeleton-input"
                                >
                                  <Controller
                                    name="fileteredMedicineMedicineDoseColl"
                                    render={({ field }) => (
                                      <CustomAsyncSelect
                                        {...field}
                                        id={"medicineDoseId"}
                                        placeholder="Search or create Medicine Dose"
                                        FieldName={
                                          "fileteredMedicineMedicineDoseColl"
                                        }
                                        defaultOptions={medicineDoseColl}
                                        openMenuOnFocus
                                        hideSelectedOptions
                                        isMulti
                                        isClearable={false}
                                        handleChange={(e) => {
                                          let length = e?.length || 0;
                                          if (length > 0) {
                                            handleMedicineDoseAdd(
                                              e[length - 1],
                                            );
                                          }
                                        }}
                                        onCreateOption={(value) => {
                                          handleClickOpen(
                                            true,
                                            value,
                                            "MEDICINE_DOSE",
                                            methods?.getValues()
                                              ?.genericNameId || null,
                                            0,
                                          );
                                        }}
                                        controlShouldRenderValue={false}
                                        closeMenuOnSelect={false}
                                        backspaceRemovesValue={false}
                                        isSearchable
                                        loadOptions={(input, callback) => {
                                          loadMedicineDoseList(
                                            methods.getValues("genericNameId")
                                              ?.value ?? "0",
                                            input?.trim(),
                                            callback,
                                          );
                                        }}
                                        menuPortalTarget={document.querySelector(
                                          "body",
                                        )}
                                        value={
                                          filteredMedicineMedicineDoseColl || []
                                        }
                                        CustomOptionComponent={
                                          medicineDoseOptionComponent
                                        }
                                        customMenuList={
                                          medicineDoseMenuListComponent
                                        }
                                      />
                                    )}
                                  />
                                </SkeletonWrapper>
                              </FormControl>
                            ) : (
                              <Typography className="danger-color">
                                Generic Name is required to create or select
                                dose, please choose Generic Name from the select
                                list above next to Brand Name.
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    {(methods.getValues("previousMedicineId") ?? 0) > 0 &&
                      !isForPopUp && (
                        <Box className="previous-next-button">
                          <Tooltip title="Previous" placement="top">
                            <IconButton
                              disableRipple
                              onClick={() => {
                                handlePreviousOrNExtMedicine(
                                  methods.getValues("previousMedicineId"),
                                );
                              }}
                            >
                              <ArrowBackIosNewRoundedIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      width: "100%",
                      justifyContent: "end",
                      gap: 2,
                    }}
                  >
                    {(methods.getValues("previousMedicineId") ?? 0) > 0 &&
                      !isForPopUp && (
                        <LoadingButton
                          color="primary"
                          startIcon={<></>}
                          variant="contained"
                          type="submit"
                          className="primary-button"
                          name="save"
                          onClick={(e) => {
                            setParam({ ...param, saveType: "SAVE_&_PREVIOUS" });
                          }}
                          disabled={showSkeleton || isLoading}
                        >
                          {(medicineId > 0 ? "Update" : "Save") +
                            " & Previous "}
                        </LoadingButton>
                      )}
                    {!isForPopUp && (
                      <LoadingButton
                        color="primary"
                        loadingPosition="start"
                        startIcon={<></>}
                        variant="contained"
                        type="submit"
                        className="primary-button"
                        name="save"
                        onClick={(e) => {
                          setParam({ ...param, saveType: "SAVE_&_COPY" });
                        }}
                        disabled={showSkeleton || isLoading}
                      >
                        {(medicineId > 0 ? "Update" : "Save") + " & Copy"}
                      </LoadingButton>
                    )}

                    <LoadingButton
                      color="primary"
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="contained"
                      type="submit"
                      className="primary-button"
                      name="save"
                      onClick={(e) => {
                        console.log(methods.formState.errors);
                        setParam({ ...param, saveType: "SAVE" });
                      }}
                      disabled={showSkeleton || isLoading}
                    >
                      {medicineId > 0 ? "Update" : "Save"}
                    </LoadingButton>

                    {(methods.getValues("nextMedicineId") ?? 0) > 0 &&
                      !isForPopUp && (
                        <LoadingButton
                          color="primary"
                          loadingPosition="start"
                          startIcon={<></>}
                          variant="contained"
                          type="submit"
                          className="primary-button"
                          name="save"
                          onClick={(e) => {
                            setParam({ ...param, saveType: "SAVE_&_NEXT" });
                          }}
                          disabled={showSkeleton || isLoading}
                        >
                          {(medicineId > 0 ? "Update" : "Save") + " & Next"}
                        </LoadingButton>
                      )}

                    <LoadingButton
                      color="primary"
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="outlined"
                      type="button"
                      className="secondary-button"
                      name="cancel"
                      onClick={handleBack}
                    >
                      Cancel
                    </LoadingButton>
                    {!isForPopUp && (
                      <Box
                        sx={{
                          display: smd ? "none" : "block",
                        }}
                      >
                        {(methods.getValues("nextMedicineId") ?? 0) > 0 && (
                          <Box className="previous-next-button">
                            <Tooltip title="Next" placement="top">
                              <IconButton
                                disableRipple
                                onClick={() => {
                                  handlePreviousOrNExtMedicine(
                                    methods.getValues("nextMedicineId"),
                                  );
                                }}
                              >
                                <ArrowForwardIosRoundedIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "MEDICINE_GENERIC"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "600px",
              height: "350px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medicine Generic Name
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicineGenericNameSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "MEDICINE_COMPANY"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "600px",
              height: "350px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medicine Company
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicineCompanySave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "MEDICINE_GROUP"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "600px",
              height: "350px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medicine Group
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicineGroupSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "MEDICINE_FORMULATION"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "600px",
              height: "350px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Medicine Formulation
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicineFormulationSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "MEDICINE_DOSE"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "1300px",
              height: "650px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                {customPopUp?.medicineDoseId > 0 ? "Update" : "Save"} Medicine
                Dose
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <MedicineDoseSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
              genericNameObject={customPopUp.genericNameObject}
              isFrom="MEDICINE"
              popUpMedicineDoseId={customPopUp?.medicineDoseId || 0}
            />
          </DialogContent>
          <Divider />
        </Dialog>
      </Box>
    </Box>
  );
}
